<template>
  <Breadcrumb></Breadcrumb>
  <div v-if="rentalListingAgreement">
    <div class="grid grid-cols-3 items-start gap-4">
      <div class="col-span-2 space-y-4">
        <!-- Title block -->
        <DetailCard>
          <template #default>
            <div class="flex gap-2 items-center">
              <div class="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center">
                <BriefcaseIcon class="h-8 w-8 text-gray-700"></BriefcaseIcon>
              </div>
              <RentalListingAgreementId :rentalListingAgreement="rentalListingAgreement" />
              <div>
                <RiskScoringLabel :level="titleRiskLevel" />
              </div>
              <router-link class="text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'edit-rental-listing-agreement', params: { id: rentalListingAgreement.id } }">
                <PencilSquareIcon class="size-5" aria-hidden="true" />
              </router-link>
            </div>
            <div v-if="pendingApprovals.length" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
              <ExclamationTriangleIcon class="h-5 w-5 text-yellow-600 mt-0.5" />
              <p class="text-sm text-yellow-800">
                <span v-for="(party, index) in pendingApprovals" :key="party.id">
                  <strong>{{ party.name }}</strong> ({{ party.role }})<span v-if="index < pendingApprovals.length - 1">, </span>
                </span>
                {{ $ngettext("n'a pas encore de profil de risque approuvé", "n'ont pas encore de profil de risque approuvé", pendingApprovals.length) }}
              </p>
            </div>
          </template>
        </DetailCard>

        <!-- Properties block -->
        <DetailCard :title="$gettext('Propriété(s)')">
          <ConfirmationDialog
            v-model="dialogOpen"
            :title="$gettext('Supprimer le bail')"
            :message="$gettext('Êtes-vous sûr de vouloir supprimer ce bail ? Cette action est irréversible.')"
            :confirm-text="$gettext('Supprimer')"
            :cancel-text="$gettext('Annuler')"
            @confirm="deleteLeaseAgreement(currentId, currentRentalListingAgreementId, currentPropertyId)"
          />
          <div v-for="rentalListingAgreementProperty in rentalListingAgreement?.relationships?.rentalListingAgreementProperties?.data" :key="rentalListingAgreementProperty.id" class="overflow-hidden border border-gray-100 rounded-lg bg-white p-4 mb-2 mt-2">
            <h3 class="text-md font-semibold text-gray-900 flex items-center justify-between w-full">
              <div class="flex items-center">
                <HomeIcon class="h-6 w-6 text-gray-700 flex-shrink-0 mr-2" />
                <span class="text-gray-900">
                  {{ rentalListingAgreementProperty.relationships.property.data.attributes.addressStreet }},
                  {{ rentalListingAgreementProperty.relationships.property.data.attributes.addressZipcode }}
                  {{ rentalListingAgreementProperty.relationships.property.data.attributes.addressCity }}
                </span>
              </div>

              <span class="text-sm text-gray-600 ml-4">
                <template v-if="rentalListingAgreementProperty.relationships?.property?.data?.attributes?.propertyTypeName">{{ rentalListingAgreementProperty.relationships.property.data.attributes.propertyTypeName }} · </template>
                <MoneySpan :value="rentalListingAgreementProperty.attributes.listingRent" />
              </span>
            </h3>

            <div class="mt-3">
              <iframe
                class="w-full h-48 rounded-md border"
                loading="lazy"
                allowfullscreen
                referrerpolicy="no-referrer-when-downgrade"
                :src="`https://www.google.com/maps?q=${encodeURIComponent(
                  rentalListingAgreementProperty.relationships.property.data.attributes.addressStreet +
                  ' ' +
                  rentalListingAgreementProperty.relationships.property.data.attributes.addressZipcode +
                  ' ' +
                  rentalListingAgreementProperty.relationships.property.data.attributes.addressCity
                )}&output=embed`">
              </iframe>

              <!-- Transaction stepper -->
              <TransactionStepper class="mt-4" :steps="transactionSteps(rentalListingAgreementProperty)" />

              <!-- Lease Agreement -->
              <div class="rounded-lg border border-gray-100 bg-gray-50 p-4 mt-2">
                <div v-if="leaseAgreement(rentalListingAgreementProperty)?.id">
                  <strong class="text-sm text-gray-900">{{ $gettext("Bail") }}</strong>
                  <DescriptionList class="mt-3">
                      <DescriptionTerm>{{ $gettext("Date de signature") }}</DescriptionTerm>
                      <DescriptionDetails>{{ parseDate(leaseAgreement(rentalListingAgreementProperty).attributes.signatureDate) }}</DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Type de bail") }}</DescriptionTerm>
                      <DescriptionDetails>{{ leaseAgreement(rentalListingAgreementProperty).relationships.leaseType.data.attributes.name }}</DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Loyer") }}</DescriptionTerm>
                      <DescriptionDetails>
                        <MoneySpan :value="leaseAgreement(rentalListingAgreementProperty).attributes.price" />
                      </DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Date de fin") }}</DescriptionTerm>
                      <DescriptionDetails>{{ parseDate(leaseAgreement(rentalListingAgreementProperty).attributes.endDate) }}</DescriptionDetails>

                      <DescriptionTerm>{{ $gettext("Locataires") }}</DescriptionTerm>
                      <DescriptionDetails>
                        <ul class="mb-1 space-y-1">
                          <li
                            v-for="tenant in leaseAgreement(rentalListingAgreementProperty).relationships.tenants.data"
                            :key="tenant.id"
                          >
                            <CustomerLink :customer="tenant" :showRiskLabel="true"></CustomerLink>
                          </li>
                        </ul>
                      </DescriptionDetails>
                    </DescriptionList>
                    <ActionBar>
                      <router-link
                        :to="{ name: 'edit-lease-agreement', params: { id: leaseAgreement(rentalListingAgreementProperty)?.id }, query: { rentalListingAgreementId: rentalListingAgreement.id, propertyId: rentalListingAgreementProperty.relationships.property.data.id }}"
                        class="inline-flex items-center rounded-md border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                      >
                        <PencilIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                        {{ $gettext("Modifier") }}
                      </router-link>
                      <button
                        @click="askDeleteLeaseAggreement(leaseAgreement(rentalListingAgreementProperty)?.id, rentalListingAgreement.id, rentalListingAgreementProperty.relationships.property.data.id)"
                        type="button"
                        class="inline-flex items-center rounded-md border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                      >
                        <TrashIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                        {{ $gettext("Supprimer") }}
                      </button>
                    </ActionBar>
                </div>
                <router-link v-else :to="{ name: 'new-lease-agreement', query: {rentalListingAgreementId: rentalListingAgreement.id, propertyId: rentalListingAgreementProperty.relationships.property.data.id}}"
                  class="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                  <PlusIcon class="-ml-0.5 mr-1.5 size-5" aria-hidden="true" />
                  {{ $gettext("Nouveau bail") }}
                </router-link>
              </div>
            </div>
          </div>
        </DetailCard>
      </div>

      <div class="col-span-1 flex flex-col gap-4">
        <!-- Completion Score block -->
        <DetailCard v-if="completionScore" :title="$gettext('Complétude du dossier')">
          <CompletionScoreAccordion :completionScore="completionScore" />
        </DetailCard>

        <!-- Summary block -->
        <DetailCard :title="$gettext('Aperçu')">
          <DescriptionList>
            <DescriptionTerm>{{ $gettext("Commission") }}</DescriptionTerm>
            <DescriptionDetails>{{ rentalListingAgreement.attributes.commission }} {{ commissionTypeSymbol(rentalListingAgreement.relationships.commissionType.data?.attributes?.name) }}</DescriptionDetails>

            <DescriptionTerm>{{ $gettext("Date de fin") }}</DescriptionTerm>
            <DescriptionDetails>{{ parseDate(rentalListingAgreement.attributes.endAt) }}</DescriptionDetails>

            <DescriptionTerm>{{ $gettext("Bailleur(s)") }}</DescriptionTerm>
            <DescriptionDetails>
              <ul>
                <li
                  v-for="rentalListingAgreementLandlord in rentalListingAgreement.relationships.rentalListingAgreementLandlords.data"
                  :key="rentalListingAgreementLandlord.id"
                >
                  <CustomerLink :customer="rentalListingAgreementLandlord" :showRiskLabel="true" :truncate="true"></CustomerLink>
                </li>
              </ul>
            </DescriptionDetails>
          </DescriptionList>
        </DetailCard>

        <!-- Risk block -->
        <DetailCard>
          <template #header>
            <span class="text-m font-semibold text-gray-900">{{ $gettext("Profil de risque") }}</span>
            <router-link class="p-2 text-indigo-600 hover:text-indigo-900 ml-auto transition-transform duration-200 hover:scale-110" :to="{ name: 'rental-listing-agreement-risk-profile', params: { id: rentalListingAgreementId } }">
              <PencilSquareIcon class="size-5" aria-hidden="true" />
            </router-link>
          </template>
          <div v-if="riskScoring !== null">
            <RiskScoringDescription :riskScoring="riskScoring"></RiskScoringDescription>
          </div>
          <div class="h-16 w-full flex items-center justify-center" v-else>
            <RiskScoringLabel cssClasses="h-full w-full flex items-center justify-center font-bold" :level="rentalListingAgreement.relationships?.lastRiskScoring?.data?.attributes?.scoreLevel ?? undefined" />
          </div>
          <template #footer>
            <RiskScoringChart v-if="riskScoring" :riskScoring="riskScoring" />
          </template>
        </DetailCard>

        <!-- Documents block -->
        <DetailCard>
          <div v-if="documents">
            <DocumentList :documents="documents" />
          </div>
        </DetailCard>

        <!-- Reports block -->
        <DetailCard>
          <ReportList :reports="rentalListingAgreementReportStore.all" report-type="rentalListingAgreement" @build="onBuildReport" @delete="onDeleteReport"></ReportList>
        </DetailCard>
      </div>
    </div>
  </div>
</template>

<script setup>
import Breadcrumb from "@/components/layout/BreadcrumbNavigation.vue";

import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import RiskScoringLabel from "@/components/risk-scorings/RiskScoringLabel.vue";
import RiskScoringDescription from "@/components/risk-scorings/RiskScoringDescription.vue";
import RiskScoringChart from "@/components/risk-scorings/RiskScoringChart.vue";
import { PlusIcon, HomeIcon, TrashIcon, PencilIcon, BriefcaseIcon, PencilSquareIcon, ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import { useRiskScoring } from "@/stores/riskScoring";
import { useRentalListingAgreementStore } from "@/stores/rentalListingAgreement";
import { useLeaseAgreementStore } from "@/stores/leaseAgreement";
import CustomerLink from "@/components/customers/CustomerLink.vue";
import { DescriptionList, DescriptionTerm, DescriptionDetails, ConfirmationDialog, MoneySpan, DetailCard, TransactionStepper, ActionBar } from "@/components/ui";
import { useDate } from "@/utils/stores/date";
const { parseDate } = useDate();
import ReportList from "@/components/reports/ReportList.vue";
import DocumentList from "@/components/documents/DocumentList.vue";
import { useRentalListingAgreementReportStore } from "@/stores/rentalListingAgreementReport";
import { useDocumentUtils } from "@/utils/documents";
import { useRentalListingAgreementPartiesUtils } from "@/utils/rental_listing_agreement_parties";
import { commissionTypeSymbol } from "@/utils/commissionType";
import RentalListingAgreementId from "@/components/rental-listing-agreements/RentalListingAgreementId.vue";
import CompletionScoreAccordion from "@/components/completion-score/CompletionScoreAccordion.vue";
import { useGettext } from "vue3-gettext";

const { $gettext } = useGettext();

const route = useRoute();
const rentalListingAgreementId = route.params.id;
const rentalListingAgreement = ref(null);
const rentalListingAgreementStore = useRentalListingAgreementStore();
const leaseAgreementStore = useLeaseAgreementStore();
const dialogOpen = ref(false);
const currentId = ref(null);
const currentRentalListingAgreementId = ref(null);
const currentPropertyId = ref(null);
const rentalListingAgreementReportStore = useRentalListingAgreementReportStore(rentalListingAgreementId);
const riskScoringStore = useRiskScoring("rental-listing-agreements", rentalListingAgreementId);
const documents = ref(null);
const pendingApprovals = ref([]);

const titleRiskLevel = computed(() => {
  const lastRiskScoring = rentalListingAgreement.value?.relationships?.lastRiskScoring?.data;
  const riskScoringConfirmed = lastRiskScoring?.attributes?.reviewApproval === true;
  const hasPendingPartyApprovals = (pendingApprovals.value?.length ?? 0) > 0;

  if (!riskScoringConfirmed || hasPendingPartyApprovals) {
    return undefined;
  }

  return lastRiskScoring?.attributes?.scoreLevel;
});

const riskScoring = computed(() => {
  return riskScoringStore.latest;
});

const reportIncludes = [
  "rentalListingAgreementLandlords",
  "rentalListingAgreementProperties",
  "rentalListingAgreementReports",
  "commissionType",
  "rentalListingAgreementDocuments",
  "lastRiskScoring"
];

const transactionSteps = (rentalListingAgreementProperty) => {
  const hasLease = !!leaseAgreement(rentalListingAgreementProperty)?.id;

  return [
    { label: $gettext("Mission de location"), completed: true, active: false },
    { label: $gettext("Bail"), completed: hasLease, active: !hasLease },
  ];
};

const leaseAgreement = (rentalListingAgreementProperty) => {
  return rentalListingAgreementProperty.relationships.property.data.relationships.leaseAgreements.data[0];
};

const askDeleteLeaseAggreement = (id, rentalListingAgreementId, propertyId) => {
  currentId.value = id;
  currentRentalListingAgreementId.value = rentalListingAgreementId;
  currentPropertyId.value = propertyId;
  dialogOpen.value = true;
};

const deleteLeaseAgreement = async (id, rentalListingAgreementId, propertyId) => {
  await leaseAgreementStore.destroy(id, rentalListingAgreementId, propertyId);
  loadRentalListingAgreement();
};

const completionScore = computed(() => {
  return rentalListingAgreement.value?.attributes?.completionScore ?? null;
});

const loadRentalListingAgreement = async () => {
  rentalListingAgreement.value = await rentalListingAgreementStore.fetchOne(
    rentalListingAgreementId, null,
    ["completionScore", "rentalListingAgreementLandlords", "rentalListingAgreementProperties", "rentalListingAgreementReports", "commissionType", "rentalListingAgreementDocuments", "lastRiskScoring"]
  );
  documents.value = useDocumentUtils().extractDocuments(rentalListingAgreement.value);
  pendingApprovals.value = useRentalListingAgreementPartiesUtils().extractPendingReviewApprovals(rentalListingAgreement.value);
};

const onBuildReport = async () => {
  await rentalListingAgreementStore.buildReport(rentalListingAgreementId, () => {
    rentalListingAgreementReportStore.fetchAll();
  }, null, reportIncludes);
};

const onDeleteReport = async (report) => {
  await rentalListingAgreementStore.deleteReport(report.id, rentalListingAgreementId, () => {
    rentalListingAgreementReportStore.fetchAll();
  });
};

onMounted(async () => {
  await loadRentalListingAgreement();
  await rentalListingAgreementReportStore.fetchAll();
  await rentalListingAgreementReportStore.watchResourceChanges(() => {
    rentalListingAgreementReportStore.fetchAll();
  });
});

</script>
